// src/ContinuousProfiling/consts.ts
var ProfileTypeNames = {
  CPU: "cpu",
  ALLOC_SPACE: "alloc_space",
  INUSE_SPACE: "inuse_space",
  SPACE: "space",
  // INUSE_SPACE is sent by golang probe, SPACE is sent by nodejs probe. Both are identical
  ALLOC_OBJECTS: "alloc_objects",
  INUSE_OBJECTS: "inuse_objects",
  GOROUTINE: "goroutine",
  WALL_TIME: "wall-time",
  WALL: "wall"
};
var ProfileTypeNamesValues = Object.values(ProfileTypeNames);
var ProfileTypeAggregations = {
  SUM: "sum",
  AVG: "avg"
};
var ProfileTypeAggregationsValues = Object.values(ProfileTypeAggregations);
var ProfileTypeUnits = {
  BYTE: "bytes",
  MILLISECOND: "milliseconds",
  MICROSECOND: "microseconds",
  NANOSECOND: "nanoseconds",
  UNIT: "unit",
  COUNT: "count",
  BYTE_PER_SECOND: "bytes_per_second",
  MILLISECOND_PER_SECOND: "milliseconds_per_second",
  MICROSECOND_PER_SECOND: "microseconds_per_second",
  NANOSECOND_PER_SECOND: "nanoseconds_per_second",
  UNIT_PER_SECOND: "unit_per_second",
  COUNT_PER_SECOND: "count_per_second"
};
var ProfileTypeUnitsValues = Object.values(ProfileTypeUnits);
var ApplicationLanguages = {
  MIXED: "mixed",
  PYTHON: "python",
  GOLANG: "go",
  PHP: "php",
  NODEJS: "javascript",
  // Taken from https://github.com/blackfireio/node-continuous-profiling/blob/v0.1.1/src/index.js#L59
  RUBY: "ruby",
  RUST: "rust",
  JAVA: "jvm",
  UNKNOWN: ""
};
var ApplicationLanguagesValues = Object.values(ApplicationLanguages);

// src/Formatter/utils.ts
var intlFormatter = new Intl.NumberFormat("en");
function formatNumber(value) {
  if (Number.isNaN(value)) {
    return `${value}`;
  }
  return intlFormatter.format(value).replace(/,/g, "\xA0");
}
function formatNumberTruncated(value, significativeNumbers = 3, truncate = 3) {
  if (value === 0 || value === 0) {
    return `0`;
  }
  const power = significativeNumbers - Math.ceil(Math.log(Math.abs(value)) / Math.LN10);
  const magnitude = 10 ** power;
  const shifted = Math.round(magnitude * value);
  const result = shifted / magnitude;
  if (Math.floor(Math.abs(result)).toString().length >= significativeNumbers) {
    return formatNumber(Math.round(result));
  }
  return formatNumber(Math.round(result * 10 ** truncate) / 10 ** truncate);
}

// src/Formatter/BaseFormatter.ts
var BaseFormatter = class {
  toStandardString(value) {
    const { standardValue, standardUnit } = this.toStandardComponents(value);
    if (!standardUnit.length) {
      return standardValue;
    }
    return `${standardValue}${standardUnit}`;
  }
  toStandardComponents(value) {
    return {
      standardValue: formatNumber(value),
      standardUnit: ""
    };
  }
  toCondensedString(value) {
    const { condensedValue, condensedUnit } = this.toCondensedComponents(value);
    if (!condensedUnit.length) {
      return condensedValue;
    }
    return `${condensedValue}${condensedUnit}`;
  }
  toCondensedComponents(value) {
    const absValue = Math.abs(value);
    if (absValue >= 1e9) {
      return {
        condensedValue: formatNumberTruncated(value / 1e9, 2, 2),
        condensedUnit: "G"
      };
    }
    if (absValue >= 1e6) {
      return {
        condensedValue: formatNumberTruncated(value / 1e6, 2, 2),
        condensedUnit: "M"
      };
    }
    if (absValue >= 1e3) {
      return {
        condensedValue: formatNumberTruncated(value / 1e3, 2, 2),
        condensedUnit: "k"
      };
    }
    return {
      condensedValue: formatNumberTruncated(value, 2, 2),
      condensedUnit: ""
    };
  }
  toComponents(value) {
    return {
      ...this.toStandardComponents(value),
      ...this.toCondensedComponents(value)
    };
  }
};

// src/Formatter/ByteFormatter.ts
var ByteFormatter = class extends BaseFormatter {
  toStandardComponents(value) {
    return {
      standardValue: formatNumber(value),
      standardUnit: "B"
    };
  }
  toCondensedComponents(value) {
    const absValue = Math.abs(value);
    if (absValue >= 1024 * 1024 * 1024 / 2) {
      return {
        condensedValue: formatNumberTruncated(value / 1024 / 1024 / 1024, 2, 2),
        condensedUnit: "GiB"
      };
    }
    if (absValue >= 1024 * 1024 / 2) {
      return {
        condensedValue: formatNumberTruncated(value / 1024 / 1024, 2, 2),
        condensedUnit: "MiB"
      };
    }
    if (absValue >= 1024 / 2) {
      return {
        condensedValue: formatNumberTruncated(value / 1024, 2, 2),
        condensedUnit: "KiB"
      };
    }
    return {
      condensedValue: formatNumberTruncated(value, 2, 2),
      condensedUnit: "B"
    };
  }
};

// src/Formatter/DurationFormatter.ts
var DurationFormatter = class extends BaseFormatter {
  toStandardComponents(value) {
    return {
      standardValue: formatNumber(value),
      standardUnit: "\xB5s"
    };
  }
  toCondensedString(value) {
    const absValue = Math.abs(value);
    if (absValue < 6e7) {
      return super.toCondensedString(value);
    }
    const seconds = Math.round(absValue / 1e6 % 60);
    const minutes = Math.floor(absValue / 6e7 % 60);
    const hours = Math.floor(absValue / 36e8);
    const parts = [];
    if (hours > 0) {
      parts.push(`${hours}h`);
    }
    if (minutes > 0) {
      parts.push(`${minutes}min`);
    }
    if (seconds > 0 && hours === 0) {
      parts.push(`${seconds}s`);
    }
    return `${value < 0 ? "-" : ""}${parts.join("\xA0")}`;
  }
  toCondensedComponents(value) {
    const absValue = Math.abs(value);
    if (absValue >= 36e8) {
      return {
        condensedValue: formatNumberTruncated(value / 36e8, 1, 1),
        condensedUnit: "h"
      };
    }
    if (absValue >= 6e7) {
      return {
        condensedValue: formatNumberTruncated(value / 6e7, 1, 1),
        condensedUnit: "min"
      };
    }
    if (absValue >= 1e6) {
      return {
        condensedValue: formatNumberTruncated(value / 1e6, 2, 2),
        condensedUnit: "s"
      };
    }
    if (absValue >= 1e3) {
      return {
        condensedValue: formatNumberTruncated(value / 1e3, 2, 2),
        condensedUnit: "ms"
      };
    }
    return {
      condensedValue: formatNumberTruncated(value),
      condensedUnit: "\xB5s"
    };
  }
};

// src/Formatter/ScaleFormatter.ts
var ScaleFormatter = class extends BaseFormatter {
  multiplier;
  decorated;
  constructor(decorated, multiplier) {
    super();
    this.decorated = decorated;
    this.multiplier = multiplier;
  }
  toStandardString(value) {
    return this.decorated.toStandardString(value * this.multiplier);
  }
  toStandardComponents(value) {
    return this.decorated.toStandardComponents(value * this.multiplier);
  }
  toCondensedString(value) {
    return this.decorated.toCondensedString(value * this.multiplier);
  }
  toCondensedComponents(value) {
    return this.decorated.toCondensedComponents(value * this.multiplier);
  }
  toComponents(value) {
    return this.decorated.toComponents(value * this.multiplier);
  }
};

// src/Formatter/SuffixFormatter.ts
var SuffixFormatter = class extends BaseFormatter {
  suffix;
  pluralize;
  decorated;
  constructor(decorated, suffix, pluralize = false) {
    super();
    this.decorated = decorated;
    this.suffix = suffix;
    this.pluralize = pluralize;
  }
  toStandardComponents(value) {
    const {
      standardValue,
      standardUnit
    } = this.decorated.toStandardComponents(value);
    const suffix = `${this.suffix}${this.pluralize && Math.abs(value) !== 1 ? "s" : ""}`;
    return {
      standardValue,
      standardUnit: `${standardUnit}${suffix}`
    };
  }
  toCondensedComponents(value) {
    const {
      condensedValue,
      condensedUnit
    } = this.decorated.toCondensedComponents(value);
    const suffix = `${this.suffix}${this.pluralize && Math.abs(value) !== 1 ? "s" : ""}`;
    return {
      condensedValue,
      condensedUnit: `${condensedUnit}${suffix}`
    };
  }
};

// src/ContinuousProfiling/formaterFactory.ts
function createFormatterForProfileType(profileType) {
  if (!profileType.unit.endsWith("_per_second") && profileType.aggregation === ProfileTypeAggregations.SUM) {
    return createFormatterForProfileTypeUnit(`${profileType.unit}_per_second`);
  }
  return createFormatterForProfileTypeUnit(profileType.unit);
}
function createFormatterForProfileTypeUnit(profileTypeUnit) {
  if (profileTypeUnit.endsWith("_per_second")) {
    return new SuffixFormatter(
      createFormatterForProfileTypeUnit(profileTypeUnit.replace(/_per_second$/, "")),
      "/s"
    );
  }
  switch (profileTypeUnit) {
    case ProfileTypeUnits.NANOSECOND:
      return new ScaleFormatter(new DurationFormatter(), 1e-3);
    case ProfileTypeUnits.MICROSECOND:
      return new DurationFormatter();
    case ProfileTypeUnits.MILLISECOND:
      return new ScaleFormatter(new DurationFormatter(), 1e3);
    case ProfileTypeUnits.BYTE:
      return new ByteFormatter();
    case ProfileTypeUnits.UNIT:
    case ProfileTypeUnits.COUNT:
      return new BaseFormatter();
    default:
      return new BaseFormatter();
  }
}
function createFormatterForProfileTypeWithItemsSuffix(profileType) {
  const baseFormatter = createFormatterForProfileType(profileType);
  switch (profileType.name) {
    case ProfileTypeNames.CPU:
    case ProfileTypeNames.WALL_TIME:
    case ProfileTypeNames.WALL:
      return new SuffixFormatter(baseFormatter, " consumed");
    case ProfileTypeNames.INUSE_SPACE:
    case ProfileTypeNames.SPACE:
    case ProfileTypeNames.ALLOC_SPACE:
      return new SuffixFormatter(baseFormatter, " allocated");
    case ProfileTypeNames.INUSE_OBJECTS:
      return new SuffixFormatter(baseFormatter, " allocated object", true);
    case ProfileTypeNames.ALLOC_OBJECTS:
      return new SuffixFormatter(baseFormatter, " allocation", true);
    case ProfileTypeNames.GOROUTINE:
      return new SuffixFormatter(baseFormatter, " in use");
    default:
      return baseFormatter;
  }
}

// src/ContinuousProfiling/profileTypeHelpers.ts
function getProfileTypeColor(profileType) {
  switch (profileType) {
    case ProfileTypeNames.CPU:
      return "#39BEBE";
    case ProfileTypeNames.ALLOC_SPACE:
      return "#3A7DFF";
    case ProfileTypeNames.INUSE_SPACE:
    case ProfileTypeNames.SPACE:
      return "#2F2F7E";
    case ProfileTypeNames.ALLOC_OBJECTS:
      return "#FF7F55";
    case ProfileTypeNames.INUSE_OBJECTS:
      return "#653193";
    case ProfileTypeNames.GOROUTINE:
      return "#EEBC08";
    case ProfileTypeNames.WALL_TIME:
    case ProfileTypeNames.WALL:
      return "#E35F94";
    // In case profileType is not recognized
    default:
      return "#ACACCB";
  }
}
function getProfileTypeLightColor(profileType) {
  switch (profileType) {
    case ProfileTypeNames.CPU:
      return "#B0E5E5";
    case ProfileTypeNames.ALLOC_SPACE:
      return "#B0CBFF";
    case ProfileTypeNames.INUSE_SPACE:
    case ProfileTypeNames.SPACE:
      return "#ACACCB";
    case ProfileTypeNames.ALLOC_OBJECTS:
      return "#FFCCBB";
    case ProfileTypeNames.INUSE_OBJECTS:
      return "#C1ADD4";
    case ProfileTypeNames.GOROUTINE:
      return "#F8E49C";
    case ProfileTypeNames.WALL_TIME:
    case ProfileTypeNames.WALL:
      return "#F1AFC9";
    default:
      return "";
  }
}
function getProfileTypeTitle(profileType) {
  switch (profileType) {
    case ProfileTypeNames.CPU:
      return "CPU Time";
    case ProfileTypeNames.ALLOC_SPACE:
      return "Allocated Memory";
    case ProfileTypeNames.INUSE_SPACE:
    case ProfileTypeNames.SPACE:
      return "Heap Live Size";
    case ProfileTypeNames.ALLOC_OBJECTS:
      return "Allocations";
    case ProfileTypeNames.INUSE_OBJECTS:
      return "Heap Live Objects";
    case ProfileTypeNames.GOROUTINE:
      return "Goroutines";
    case ProfileTypeNames.WALL_TIME:
    case ProfileTypeNames.WALL:
      return "Wall Time";
    default:
      return null;
  }
}
function getProfileTypeDescription(profileType) {
  switch (profileType) {
    case ProfileTypeNames.CPU:
      return "Time spent running on the CPU";
    case ProfileTypeNames.ALLOC_SPACE:
      return "Number of bytes allocated in memory";
    case ProfileTypeNames.INUSE_SPACE:
    case ProfileTypeNames.SPACE:
      return "Number of bytes allocated that are not yet garbage collected";
    case ProfileTypeNames.ALLOC_OBJECTS:
      return "Number of objects allocated in memory";
    case ProfileTypeNames.INUSE_OBJECTS:
      return "Number of objects allocated that are not yet garbage collected";
    case ProfileTypeNames.GOROUTINE:
      return "Number of goroutines (both on-CPU and off-CPU)";
    case ProfileTypeNames.WALL_TIME:
    case ProfileTypeNames.WALL:
      return "Elapsed total time";
    default:
      return null;
  }
}
function getProfileTypeItems(profileType) {
  switch (profileType) {
    case ProfileTypeNames.ALLOC_SPACE:
    case ProfileTypeNames.INUSE_SPACE:
    case ProfileTypeNames.SPACE:
      return "bytes";
    case ProfileTypeNames.ALLOC_OBJECTS:
    case ProfileTypeNames.INUSE_OBJECTS:
      return "objects";
    case ProfileTypeNames.GOROUTINE:
      return "goroutines";
    case ProfileTypeNames.CPU:
    case ProfileTypeNames.WALL_TIME:
    case ProfileTypeNames.WALL:
      return "time";
    default:
      return "";
  }
}
var ProfileTypeNamesPriorities = {
  [ProfileTypeNames.CPU]: 6,
  [ProfileTypeNames.WALL_TIME]: 5,
  [ProfileTypeNames.WALL]: 5,
  [ProfileTypeNames.ALLOC_SPACE]: 4,
  [ProfileTypeNames.INUSE_SPACE]: 4,
  [ProfileTypeNames.SPACE]: 4,
  [ProfileTypeNames.ALLOC_OBJECTS]: 3,
  [ProfileTypeNames.INUSE_OBJECTS]: 3,
  [ProfileTypeNames.GOROUTINE]: 3
};
function getProfileTypesSorted(profileTypes) {
  return profileTypes.toSorted((a, b) => {
    const prioA = ProfileTypeNamesPriorities[a.name] ?? 0;
    const prioB = ProfileTypeNamesPriorities[b.name] ?? 0;
    if (prioA === prioB) {
      return a.name.localeCompare(b.name);
    }
    return prioA > prioB ? -1 : 1;
  });
}
function getApplicationLanguage(application) {
  const languages = application.languages.filter(
    (e) => e !== ApplicationLanguages.UNKNOWN
  );
  if (languages.length === 0) {
    return void 0;
  }
  if (languages.length === 1) {
    return languages[0];
  }
  return ApplicationLanguages.MIXED;
}
function getDefaultProfileType(application) {
  switch (getApplicationLanguage(application)) {
    case ApplicationLanguages.PHP:
      return application.profile_types[ProfileTypeNames.WALL_TIME] ?? Object.values(application.profile_types)[0];
    case ApplicationLanguages.GOLANG:
    case ApplicationLanguages.PYTHON:
    case ApplicationLanguages.RUBY:
    case ApplicationLanguages.RUST:
    case ApplicationLanguages.JAVA:
      return application.profile_types[ProfileTypeNames.CPU] ?? Object.values(application.profile_types)[0];
    case ApplicationLanguages.NODEJS:
      return application.profile_types[ProfileTypeNames.WALL] ?? Object.values(application.profile_types)[0];
    case ApplicationLanguages.MIXED:
    default:
      return Object.values(application.profile_types)[0];
  }
}

// src/ContinuousProfiling/flamebearerConverter.ts
function convertContinuousProfilingProfile(profile, language, pruneRatio = 0.01) {
  const metricsByCall = {};
  let rootNode = void 0;
  const totalCost = profile.flamebearer.numTicks / (profile._scale_factor ?? 1);
  const inclusiveLimit = totalCost * pruneRatio;
  let parents = [];
  let level = 0;
  const calls = {};
  let index = 0;
  for (const name of profile.flamebearer.names) {
    const callId = `${index}`;
    calls[callId] = convertFlamebearerName(callId, name, language);
    index++;
  }
  for (const spans of profile.flamebearer.levels) {
    let start = 0;
    let parentsIndex = 0;
    const spansNodes = [];
    for (let i = 0; i < spans.length; i += 4) {
      const [offset, outerWidth, innerWidth, nameIndex] = spans.slice(i, i + 4);
      const inclusiveCost = outerWidth / (profile._scale_factor ?? 1);
      const exclusiveCost = innerWidth / (profile._scale_factor ?? 1);
      start += offset;
      const call = calls[`${nameIndex}`];
      metricsByCall[call.id] ??= {
        call,
        exclusive: {
          raw: 0,
          rate: 0,
          impact: 0
        },
        inclusive: {
          raw: 0,
          rate: 0,
          impact: 0
        }
      };
      const end = start + outerWidth;
      if (!rootNode) {
        rootNode = {
          callstack: [call.id],
          call,
          start,
          end,
          level,
          exclusive: exclusiveCost,
          inclusive: inclusiveCost,
          children: []
        };
        spansNodes.push(rootNode);
        start += outerWidth;
        metricsByCall[call.id].exclusive.raw += exclusiveCost;
        metricsByCall[call.id].inclusive.raw += inclusiveCost;
        continue;
      }
      while (parentsIndex < parents.length - 1 && parents[parentsIndex].end <= start) {
        parentsIndex++;
      }
      const parentNode = parents[parentsIndex];
      if (!parentNode) {
        start += outerWidth;
        metricsByCall[call.id].exclusive.raw += exclusiveCost;
        metricsByCall[call.id].inclusive.raw += inclusiveCost;
        continue;
      }
      const isRecursive = parentNode.callstack.includes(call.id);
      if (!isRecursive) {
        metricsByCall[call.id].exclusive.raw += exclusiveCost;
        metricsByCall[call.id].inclusive.raw += inclusiveCost;
      }
      if (inclusiveCost < inclusiveLimit) {
        start += outerWidth;
        continue;
      }
      const node = {
        callstack: [...parentNode.callstack, call.id],
        call,
        start,
        end,
        level,
        exclusive: exclusiveCost,
        inclusive: inclusiveCost,
        children: []
      };
      spansNodes.push(node);
      parentNode.children.push(node);
      start += outerWidth;
    }
    parents = spansNodes;
    level++;
  }
  const rateFactor = profile._aggregation_type === ProfileTypeAggregations.SUM ? (new Date(profile._to).getTime() - new Date(profile._from).getTime()) / 1e3 : 1;
  return {
    profileType: profile._profile_type,
    rootNode: rootNode ?? {
      callstack: ["0"],
      call: {
        id: "0",
        raw: "root",
        label: "root",
        components: {}
      },
      start: 0,
      end: profile.flamebearer.numTicks,
      level: 0,
      exclusive: 0,
      inclusive: totalCost,
      children: []
    },
    callsMetrics: Object.values(metricsByCall).filter((metric) => metric.inclusive.raw > inclusiveLimit).map((metric) => ({
      ...metric,
      exclusive: {
        ...metric.exclusive,
        rate: rateFactor === 0 ? 0 : metric.exclusive.raw / rateFactor,
        impact: metric.exclusive.raw / totalCost
      },
      inclusive: {
        ...metric.inclusive,
        rate: rateFactor === 0 ? 0 : metric.inclusive.raw / rateFactor,
        impact: metric.inclusive.raw / totalCost
      }
    })),
    total: totalCost
  };
}
function convertFlamebearerName(id, rawName, language) {
  let v2Name;
  try {
    v2Name = JSON.parse(rawName);
    if (v2Name.filename === "" || null === (v2Name.filename ?? null)) {
      delete v2Name.filename;
    }
    if (v2Name.line === 0 || null === (v2Name.line ?? null)) {
      delete v2Name.line;
    }
  } catch {
    v2Name = { name: rawName };
  }
  const { filename, name, line } = v2Name;
  if (name === "total") {
    return {
      id,
      raw: rawName,
      label: "Total",
      components: {}
    };
  }
  const components = getCallComponents(v2Name, language);
  const functionName = components.functionName ?? name;
  delete components.functionName;
  if (filename) {
    const basename = getBasename(filename);
    return {
      id,
      raw: rawName,
      label: line ? `${functionName} (${basename} - L#${line})` : `${functionName} (${basename})`,
      components: {
        ...components,
        file: filename
      }
    };
  }
  return {
    id,
    raw: rawName,
    label: line ? `${functionName} (L#${line})` : functionName,
    components
  };
}
function getCallComponents(call, language) {
  switch (language) {
    case ApplicationLanguages.PHP:
      return getCallComponentsForPhp(call);
    case ApplicationLanguages.GOLANG:
      return getCallComponentsForGolang(call);
    case ApplicationLanguages.NODEJS:
      return getCallComponentsForNodejs(call);
    default:
      return {};
  }
}
function getCallComponentsForPhp(v2Name) {
  const { name } = v2Name;
  if (name.includes("|")) {
    const parts = name.split("|");
    return {
      functionName: parts.at(-1) ?? "",
      module: parts[0]
    };
  }
  const pos = name.lastIndexOf("\\");
  if (pos !== -1) {
    return {
      functionName: name.substring(pos + 1),
      namespace: name.substring(0, pos)
    };
  }
  return {};
}
function getCallComponentsForGolang(v2Name) {
  const { name } = v2Name;
  if (name.split(".").length > 2) {
    const pos = name.lastIndexOf("/");
    const secondPartOfName = name.substring(pos + 1);
    const [packageName2, functionName2] = secondPartOfName.split(".", 2);
    return {
      functionName: functionName2,
      package: `${name.substring(0, pos)}/${packageName2}`
    };
  }
  const [packageName, functionName] = name.split(".", 2);
  return {
    functionName,
    package: packageName
  };
}
function getCallComponentsForNodejs(v2Name) {
  const { filename } = v2Name;
  if (filename?.includes("/node_modules/")) {
    const pathElements = filename.split("/");
    const modulesIndex = pathElements.indexOf("node_modules");
    return {
      package: pathElements[modulesIndex + 1]
    };
  }
  return {};
}
function getBasename(path) {
  return path.split("/").at(-1);
}
export {
  ApplicationLanguages,
  ApplicationLanguagesValues,
  ProfileTypeAggregations,
  ProfileTypeAggregationsValues,
  ProfileTypeNames,
  ProfileTypeNamesValues,
  ProfileTypeUnits,
  ProfileTypeUnitsValues,
  convertContinuousProfilingProfile,
  createFormatterForProfileType,
  createFormatterForProfileTypeWithItemsSuffix,
  getApplicationLanguage,
  getDefaultProfileType,
  getProfileTypeColor,
  getProfileTypeDescription,
  getProfileTypeItems,
  getProfileTypeLightColor,
  getProfileTypeTitle,
  getProfileTypesSorted
};
